import React, {useEffect, useState} from "react"
import {Btn, FormInput, FormSelect, FormTextarea, Modal} from "./";
import {useCore, useLogin} from "../hooks";
import {CustomCheckoutOrder} from "../store/core/reducer.ts";
import {toast} from "react-toastify";
import {Checkout, ExtraOperation, PaymentItem} from "depoto-core";
import {formatPrice, generateUuidV4, Schema, wait} from "../utils";

type SettlementType = 'withdrawal' | 'final' | 'deposit'
export const ModalCheckoutExtraOperation: React.FC = () => {
  const { core, checkouts, setCheckouts, setSelectedDetailCheckoutId, selectedDetailCheckoutId, currency } = useCore()
  const { user } = useLogin()
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [checkout, setCheckout] = useState<Checkout>(checkouts?.find(c => c.id === selectedDetailCheckoutId))
  const [settlementType, setSettlementType] = useState<SettlementType>('withdrawal')
  const [settlementAmount, setSettlementAmount] = useState<number>(0)
  const [settlementFinal, setSettlementFinal] = useState<number>(0)
  const [settlementNote, setSettlementNote] = useState<string>('')

  useEffect(() => {
    if (checkouts?.length && selectedDetailCheckoutId) {
      setCheckout(checkouts?.find(c => c.id === selectedDetailCheckoutId))
    }
  }, [checkouts, selectedDetailCheckoutId])

  const resetForm = () => {
    setSettlementType('withdrawal')
    setSettlementAmount(0)
    setSettlementFinal(0)
    setSettlementNote('')
  }

  const openModal = async () => {
    if (isVisible) return
    resetForm()
    setIsVisible(true)
    setIsLoading(true)
    try {
      const allowedIds = checkouts.map(c => c.id)
      const refreshedCheckouts = await core.services.checkout.getList({ filters: {} }, Schema.checkout.detail).then(r => r.items || [])
      setCheckouts(refreshedCheckouts.filter(c => c.enabled && allowedIds.includes(c.id)))
    } catch (e) {
      toast.error('Nepodařilo se načíst aktuální stav pokladny')
      setIsVisible(false)
    } finally {
      setIsLoading(false)
    }
  }

  const closeModal = () => {
    if (isSubmitting) return
    setIsVisible(false)
  }

  const reloadCheckouts = async () => {
    const checkouts = await core.services.checkout.getList({ filters: {} }, Schema.checkout.detail).then(r => r.items || [])
    const refreshedUser = await core.services.user.getMyProfile(Schema.user.detail)
    let filteredCheckouts = [...checkouts].filter(c => c.enabled)
    if (refreshedUser?.checkouts?.length) {
      const cIds = refreshedUser.checkouts.map(c => c.id)
      filteredCheckouts = checkouts.filter(c => cIds.includes(c.id))
    }
    setCheckouts(filteredCheckouts)
  }

  const reloadDetailLists = async () => {
    // lists reload on selectedDetailCheckoutId change
    setSelectedDetailCheckoutId(undefined)
    await wait(100)
    setSelectedDetailCheckoutId(selectedDetailCheckoutId)
  }

  const updateAmount = (amount: number) => {
    let finalAmount = checkout.amount - amount
    if (amount > checkout.amount) {
      amount = checkout.amount
      finalAmount = checkout.amount - amount
    }
    setSettlementAmount(Number(amount.toFixed(2)))
    setSettlementFinal(Number(finalAmount.toFixed(2)))
  }

  const updateDepositAmount = (amount: number) => {
    setSettlementAmount(Number(amount.toFixed(2)))
    setSettlementFinal(Number((checkout.amount + amount).toFixed(2)))
  }

  const updateFinalAmount = (finalAmount: number) => {
    const delta = finalAmount - checkout.amount // positive = deposit, negative = withdrawal
    setSettlementAmount(Number(delta.toFixed(2)))
    setSettlementFinal(Number(finalAmount.toFixed(2)))
  }

  const createExtraOperation = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    const extraOp = new ExtraOperation({
      checkout,
      amount: settlementType === 'final' ? settlementAmount : settlementType === 'deposit' ? settlementAmount : -settlementAmount,
      note: settlementNote,
    })
    try {
      // todo fragments. keep it simple for now, so fck zeus
      // @ts-ignore
      await core.services.checkout.createExtraOperation(extraOp)
    } catch (e) {
      toast.error('Operaci se nepodařilo vytvořit')
      setIsSubmitting(false)
      return
    }
    toast.success('Operace byla vytvořena')
    // keep the modal with loader open until the new state is loaded
    try {
      await reloadCheckouts()
    } catch (e) {
      toast.error('Nepodařilo se obnovit data pokladny')
    }
    setIsSubmitting(false)
    setIsVisible(false)
    await reloadDetailLists()
  }

  const isBusy = isLoading || isSubmitting

  return (
    <Modal
      modalOpenBtn={<Btn className="btn-primary btn-soft font-bold" icon={'fa-scale-balanced'} children={'Nastavit stav pokladny'} onClick={openModal} />}
      isVisible={isVisible}
      modalTitle={'Nastavit stav'}
      modalWidth={'sm'}
      onClose={closeModal}
      recalcHeightHash={`${isBusy}-${checkouts?.length}-${selectedDetailCheckoutId}-${settlementFinal}`}
      content={isBusy ? (
        <div className="flex flex-col items-center justify-center gap-3 py-10">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <span className="text-base-400">{isSubmitting ? 'Ukládám a načítám nový stav pokladny…' : 'Načítám aktuální stav pokladny…'}</span>
        </div>
      ) : (
        <div>
          <div role="tablist" className="tabs mb-2">
            <a role="tab" className={`tab ${settlementType === 'withdrawal' ? 'btn btn-primary tab-active' : 'btn btn-primary btn-soft'}`} onClick={() => { setSettlementType('withdrawal'); setSettlementAmount(0); setSettlementFinal(0) }}>
              Výběr
            </a>
            <a role="tab" className={`tab ${settlementType === 'deposit' ? 'btn btn-primary tab-active' : 'btn btn-primary btn-soft'}`} onClick={() => { setSettlementType('deposit'); setSettlementAmount(0); setSettlementFinal(0) }}>
              Vklad
            </a>
            <a role="tab" className={`tab ${settlementType === 'final' ? 'btn btn-primary tab-active' : 'btn btn-primary btn-soft'}`} onClick={() => { setSettlementType('final'); setSettlementAmount(0); setSettlementFinal(checkout?.amount || 0) }}>
              Výsledný stav
            </a>
          </div>
          <div>
            {settlementType === 'withdrawal' && (
              <FormInput type="number" value={`${settlementAmount}`} onChange={val => updateAmount(Number(val))} />
            )}
            {settlementType === 'final' && (
              <FormInput type="number" value={`${settlementFinal}`} onChange={val => updateFinalAmount(Number(val))} />
            )}
            {settlementType === 'deposit' && (
              <FormInput type="number" value={`${settlementAmount}`} onChange={val => updateDepositAmount(Number(val))} />
            )}
          </div>
          <FormTextarea label={'Popis'} value={settlementNote} onChange={val => setSettlementNote(val)} />
          <div>
            Aktuální stav pokladny: {formatPrice(checkout?.amount || 0, currency.id)}
          </div>
          <div>
            Vyrovnávací operace: {formatPrice(settlementType === 'withdrawal' ? -settlementAmount : settlementAmount, currency.id)}
          </div>
          <div>
            Výsledný stav pokladny: {formatPrice(
              settlementType === 'final'
                ? settlementFinal
                : settlementType === 'deposit'
                  ? (checkout?.amount || 0) + settlementAmount
                  : Math.max((checkout?.amount || 0) - settlementAmount, 0),
              currency.id
            )}
          </div>
        </div>
      )}
      footerContent={
        <div className="flex flex-row justify-between items-center">
          <Btn className="btn-base-100" icon={'fa-times'} children={'Zrušit'} isDisabled={isSubmitting} onClick={closeModal}/>
          <Btn className="btn-primary" icon={'fa-check'} children={'Nastavit stav'} isDisabled={settlementAmount === 0 || isBusy} onClick={createExtraOperation}/>
        </div>
      }
    />
  )
}
